/** Dev-only live-cached Workspace preview.
 *
 * Binds the approved Workspace mockup to existing MarkovLab cache-first GETs.
 * Does not mount on /ticker/:symbol and does not change production Workbench.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

if (typeof window !== "undefined") {
  const injected = window.sessionStorage.getItem("ml_preview_jwt");
  if (injected) api.defaults.headers.common.Authorization = `Bearer ${injected}`;
}
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { TickerWorkspace, WORKSPACE_TABS } from "@/workspace/TickerWorkspace";
import {
  CachedBadge, DECISION_STATES, DecisionBadge, EARNINGS_QUALITIES, EarningsQualityBadge,
  OPTION_STATES, OptionStateBadge, WorkspaceAdminProvider, WorkspacePanel,
} from "@/workspace/primitives";
import { useWorkspaceLive } from "@/workspace/live/useWorkspaceLive";
import { WorkspaceLiveProvenance, WorkspaceLiveRequestAudit } from "@/workspace/live/WorkspaceLiveAdmin";

export const LIVE_PREVIEW_TICKERS = ["ULTA", "AVAV", "BWXT"];

function Toggle({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[11px] px-2 py-0.5 rounded-sm border font-mono-data"
      style={{
        color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
        background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
        borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
      }}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

export default function TickerWorkspacePreviewPage() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const initial = useMemo(() => {
    const q = (params.get("ticker") || "").toUpperCase();
    return q || "ULTA";
  }, [params]);
  const initialTab = WORKSPACE_TABS.some((x) => x.value === params.get("tab"))
    ? params.get("tab")
    : "overview";
  const [symbol, setSymbol] = useState(initial);
  const [tab, setTab] = useState(initialTab);
  const [adminView, setAdminView] = useState(false);
  const live = useWorkspaceLive(symbol, tab);

  useEffect(() => {
    const q = (params.get("ticker") || "").toUpperCase();
    if (q) setSymbol(q);
    const nextTab = params.get("tab");
    if (WORKSPACE_TABS.some((x) => x.value === nextTab)) setTab(nextTab);
  }, [params]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__WORKSPACE_LIVE_AUDIT = {
      symbol,
      tab,
      requestLog: live.requestLog,
      resources: Object.fromEntries(
        Object.entries(live.resources || {}).map(([k, v]) => [k, { status: v.status, error: v.error, fetchedAt: v.fetchedAt }]),
      ),
      mapping: live.ticker?.mappingReport || null,
      alignment: live.ticker?.alignmentStats || null,
      writes: (live.requestLog || []).filter((e) => e.method !== "GET").length,
      externalProviderCalls: (live.requestLog || []).filter((e) => e.externalProvider).length,
    };
    return undefined;
  }, [symbol, tab, live.requestLog, live.resources, live.ticker]);

  return (
    <WorkspaceAdminProvider value={adminView}>
      <div
        className="min-h-screen p-3 md:p-6 max-w-[1400px] mx-auto space-y-3"
        style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-primary)" }}
        data-testid="ticker-workspace-preview"
      >
        <div
          className="border p-2.5 flex flex-wrap items-center gap-x-4 gap-y-2"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
        >
          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
            Dev preview · live-cached Workspace
          </span>
          <span className="flex items-center gap-1.5">
            {LIVE_PREVIEW_TICKERS.map((x) => (
              <Toggle key={x} label={x} active={symbol === x} onClick={() => setSymbol(x)} testid={`preview-ticker-${x}`} />
            ))}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>View as</span>
            <Toggle label="User" active={!adminView} onClick={() => setAdminView(false)} testid="preview-view-user" />
            <Toggle label="Admin" active={adminView} onClick={() => setAdminView(true)} testid="preview-view-admin" />
          </span>
          <CachedBadge text="Cached data" />
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Existing authenticated GETs only. No production route change. Fetch time is not market observation time.
          </span>
        </div>

        {authLoading && !user && !live.ticker && !live.headerError && live.loadingHeader ? (
          <WorkspacePanel title="Signing in" testid="live-auth-loading">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Preparing the session…</p>
          </WorkspacePanel>
        ) : live.loadingHeader ? (
          <WorkspacePanel title="Loading cached data" testid="live-header-loading">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
              Loading cached ticker data for {symbol}…
            </p>
          </WorkspacePanel>
        ) : live.headerError ? (
          <WorkspacePanel title="Cached data unavailable" testid="live-header-error">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
              {typeof live.headerError === "string" ? live.headerError : "data unavailable"}
            </p>
          </WorkspacePanel>
        ) : live.ticker ? (
          <TickerWorkspace ticker={live.ticker} tab={tab} onTabChange={setTab} />
        ) : null}

        {adminView ? (
          <>
            <WorkspaceLiveRequestAudit log={live.requestLog} resources={live.resources} />
            {live.ticker ? <WorkspaceLiveProvenance rows={live.ticker.provenanceRows} /> : null}
          </>
        ) : (
          <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Cached data. Timestamp unavailable fields stay labelled as such. Indicative estimate only where pricing method is modelled.
          </p>
        )}

        <WorkspacePanel title="Label gallery" subtitle="Presentation states only — not live ticker values" testid="preview-state-gallery">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[9px] uppercase tracking-wide w-28 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
                Decision state
              </span>
              {DECISION_STATES.map((s) => <DecisionBadge key={s} state={s} />)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[9px] uppercase tracking-wide w-28 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
                Options data
              </span>
              {OPTION_STATES.map((s) => <OptionStateBadge key={s} state={s} />)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[9px] uppercase tracking-wide w-28 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
                Earnings date
              </span>
              {EARNINGS_QUALITIES.map((q) => <EarningsQualityBadge key={q} quality={q} />)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[9px] uppercase tracking-wide w-28 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
                Tier meaning
              </span>
              <StatusBadge tier="info" text="Neutral or informational" />
              <StatusBadge tier="caution" text="Real caution" />
              <StatusBadge tier="blocking" text="Actual blocker" />
            </div>
          </div>
        </WorkspacePanel>
      </div>
    </WorkspaceAdminProvider>
  );
}
